import { call, put, select, takeLatest } from 'redux-saga/effects'

import VALUES from 'core/data-model/constants/values'
import { getDistributorProfile } from 'core/service/distributor'
import AuthApi from 'core/service/distributor/auth-api'
import { getCachedSelfAdvisor } from 'identity/saga'
import { christianToThaiWithoutFormat } from 'core/service/lib/date'
import { generateRiderBenefitData } from 'core/service/rider/benefits'
import { getServiceWrapper } from 'core/service-wrapper'
import {
  getIdentityProfile,
  getIdentityUser,
  getSelectedOffice,
  getUserDistributorGroup,
  getStaffInfo,
} from 'identity/selectors'
import {
  getAge,
  getBirthdate,
  getGenderLabel,
  getInsuredFirstName,
  getInsuredLastName,
} from 'quick-quote/insured-information/selectors'
import {
  getCalculatedCoveragePeriod,
  getCalculatedPaymentPeriod,
  getMappingRiderCampaignDiscountBIMessage,
} from 'quick-quote/product-common/benefit-illustration/selectors'
import { UPDATE_RIDERS_BENEFITS } from 'quick-quote/product-common/coverage-plan/actions'
import { getAvailableRiders, getSelectedRiders } from 'quick-quote/product-common/coverage-plan/selectors'
import {
  getAllocationCategoryData,
  getAllocationByInvestmentType,
  getAllocationOptions,
} from 'quick-quote/product-investment/benefit-illustration/selectors'
import {
  getSelectedModelFactorLabel,
  getRegularPremium,
  getRegularTopUp,
  getSumAssured,
  getCoverageDeathBenefit,
  getLumpSumTopUp,
} from 'quick-quote/product-investment/coverage-plan/selectors'
import { getRppFundAllocations } from 'quick-quote/product-investment/fund-allocation/rpp/selectors'
import { getAvailableFunds } from 'quick-quote/product-investment/fund-allocation/selectors'
import { getRiskEvaluated } from 'quick-quote/product-investment/risk-questionnaire/selectors'
import {
  getSelectedDisplayProductNameThai,
  getSelectedDisplayProductCode,
  getSelectedDisplayProduct,
} from 'quick-quote/product-selection/selectors'
import { getRiderBenefitTables } from 'quick-quote/product-whole-life/benefit-illustration/selectors'
import { isReviseQQFlow } from 'quick-quote/selectors'
import { getImageByName } from 'quick-quote/ui/selectors'

import { addRiderBenefit, removeRiderBenefit } from './actions'

// TODO: Review this file again
export function* getBIInvestmentProps({ isIgnoreStaffInfo = false }) {
  const serviceWrapper = getServiceWrapper()
  const basicPlan = yield select(getSelectedDisplayProduct)
  const rppChart = yield select(getImageByName('rpp'))
  const lstuChart = yield select(getImageByName('lstu'))
  const pieChartFundByInvestmentType = {
    rpp: rppChart,
    lstu: lstuChart,
  }
  const policyType = yield select(getSelectedDisplayProductNameThai)
  const productCode = yield select(getSelectedDisplayProductCode)
  const riders = yield select(getSelectedRiders)
  const age = yield select(getAge)
  const birthdate = yield select(getBirthdate)
  const genderLabel = yield select(getGenderLabel)
  const sumAssured = yield select(getSumAssured)
  const calculatedCoveragePeriod = yield select(getCalculatedCoveragePeriod)
  const calculatedPaymentPeriod = yield select(getCalculatedPaymentPeriod)
  const firstName = yield select(getInsuredFirstName)
  const lastName = yield select(getInsuredLastName)
  const insured = {
    firstName,
    lastName,
    age,
    genderLabel,
  }
  const regularPremium = yield select(getRegularPremium)
  const regularTopUp = yield select(getRegularTopUp)
  const lumpSumTopUp = yield select(getLumpSumTopUp)
  const coverageDeath = yield select(getCoverageDeathBenefit)
  const riderBenefitData = yield select(getRiderBenefitTables)
  const paymentModeLabel = yield select(getSelectedModelFactorLabel)
  let profile = yield select(getIdentityProfile)
  const user = yield select(getIdentityUser)
  const accessToken = user.access_token
  const distributorType = yield select(getUserDistributorGroup)
  const bancOffice = yield select(getSelectedOffice)
  const agentInfo = yield call(getCachedSelfAdvisor)
  profile = getDistributorProfile(profile, agentInfo, bancOffice)
  const funds = yield select(getAvailableFunds)
  const allocations = yield select(getRppFundAllocations)
  const allocationByInvestmentType = yield select(getAllocationByInvestmentType)
  const insuredRiskLevel = yield select(getRiskEvaluated)
  const allocationCategoryByInvestmentType = yield select(getAllocationCategoryData)
  const allocationOptions = yield select(getAllocationOptions)
  const getDateTimeInThai = () => christianToThaiWithoutFormat().format('Do MMMM YYYY')
  const getDateTime = () => christianToThaiWithoutFormat().format('DD/MM/YYYY')
  const isReviseQQ = yield select(isReviseQQFlow)

  const dateInThai = yield call(getDateTimeInThai)
  const datetime = yield call(getDateTime)
  const cashFlowProps = yield call(serviceWrapper.products.INVESTMENT.getCashFlowProps)
  const staff = isIgnoreStaffInfo ? { id: '', name: '', surname: '', position: '' } : yield select(getStaffInfo)
  const riderCampaignDiscountBIMessage = yield select(getMappingRiderCampaignDiscountBIMessage)
  return {
    productCategory: VALUES.INVESTMENT,
    type: 'FULL_BI',
    basicPlan,
    policyType,
    productCode,
    birthdate,
    sumAssured: sumAssured.value,
    calculatedCoveragePeriod: calculatedCoveragePeriod,
    calculatedPaymentPeriod: calculatedPaymentPeriod,
    insured,
    riders,
    riderBenefitData,
    payer: {},
    regularPremium: regularPremium.value,
    regularTopupPremium: regularTopUp.value,
    lumpSumTopUp,
    coverageDeath,
    paymentModeLabel,
    profile,
    distributorType,
    accessToken,
    dateInThai,
    datetime,
    funds,
    allocations,
    allocationByInvestmentType,
    allocationCategoryByInvestmentType,
    allocationOptions,
    insuredRiskLevel: insuredRiskLevel.value,
    cashFlowProps,
    cashFlowByExpectedReturn: {},
    pieChartFundByInvestmentType,
    staff,
    isReviseQQ,
    riderCampaignDiscountBIMessage,
  }
}

/**
 * createProductInvestmentBenefitFunc
 * @param {ReturnType<import('core/data/data-wrapper')._DB>} dataWrapper
 */
export function createProductInvestmentBenefitFunc(dataWrapper) {
  // TODO: Remove duplication to whole-life
  const addRiderBenefitFunction = function*(selectedRider, selectedRiders, age) {
    const riderCode = selectedRider.code
    const riderBenefits = yield call(dataWrapper.getRiderBenefit, riderCode)
    const riderSumAssured = selectedRider.sumAssured
    const extendedRiderBenefit = yield call(
      generateRiderBenefitData,
      {
        age: age,
        riderBenefitData: riderBenefits,
        riderSumAssured: riderSumAssured,
        selectedRiders: selectedRiders,
      },
      riderCode
    )
    yield put(addRiderBenefit(riderCode, extendedRiderBenefit))
  }

  function* addOrRemoveRiderBenefits() {
    try {
      const riders = yield select(getAvailableRiders)
      const selectedRiders = riders.filter(({ isSelected, isSelectable }) => isSelected && isSelectable)
      const nonSelectedRiders = riders.filter(({ isSelected, isSelectable }) => !(isSelected && isSelectable))

      if (selectedRiders.length > 0) {
        const age = yield select(getAge)
        yield selectedRiders.map((rider) => {
          return call(addRiderBenefitFunction, rider, selectedRiders, age)
        })
      }

      yield nonSelectedRiders.map((rider) => {
        return put(removeRiderBenefit(rider.code))
      })
    } catch (error) {}

    yield []
  }

  return {
    addOrRemoveRiderBenefits,
  }
}

/**
 * @param serviceWrapper {Awaited<ReturnType<import('core/service-wrapper/wrapper').buildServiceWrapper>>}
 */
export function* watchers(serviceWrapper) {
  const { INVESTMENT } = serviceWrapper.products
  yield [takeLatest(UPDATE_RIDERS_BENEFITS, INVESTMENT.addOrRemoveRiderBenefits)]
}

export const sagas = watchers
